import {
  createSlice,
  createSelector,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { HYDRATE } from "next-redux-wrapper";
import { createWrapper } from "main/features/AxiosWrapper";
import { decamelizeKeys } from "humps";
import { stringify } from "qs";
import { PreBooking, PreBookingState } from "main/types/preBooking";
import { ErrorResponse } from "main/types/errorResponse";
import {
  isPendingAction,
  isRejectedAction,
  isFulfilledAction,
  deepMerge,
} from "main/utils/sliceUtil";
import { API_BASE_PATH } from "main/constants/constant";
import { HotelFacilityTypes } from "main/constants/hotelFacilityTypes";
import { BookingTranslationTypes } from "../constants/bookingTranslationTypes";
import { RootState } from "main/store";

const key = "preBooking";

const initialState: PreBookingState = {
  preBooking: null,
  error: null,
  loading: false,
  param: {},
};

/** Async * */
export const fetchPreBooking = createAsyncThunk<
  {
    preBooking: PreBooking;
  },
  {
    id: string;
    hotelId: string;
    param?: any;
    headers?: any;
    locale?: string;
    isServerSide?: boolean;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchPreBooking`, async (args, { rejectWithValue }) => {
  try {
    const { id, hotelId, param, isServerSide, headers, locale } = args;
    const paramStr: string = param
      ? stringify(decamelizeKeys(param), {
          arrayFormat: "brackets",
        })
      : "";
    const result = await createWrapper({ isServerSide, locale }).get(
      `${API_BASE_PATH}/v1/hotels/${hotelId}/pre_bookings/${id}.json?${paramStr}`,
      {
        headers,
      },
    );
    return {
      preBooking: result.data.preBooking,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postPreBooking = createAsyncThunk<
  {
    preBooking: PreBooking;
  },
  {
    hotelId: string;
    hotelRoomId: number;
    hotelPlanId: number;
    checkinDate: string;
    checkoutDate: string;
    guests: any[];
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postPreBooking`, async (args, { rejectWithValue }) => {
  try {
    const { hotelId, ...rest } = args;
    const params = decamelizeKeys({ hotelCode: hotelId, ...rest });
    const result = await createWrapper().post(
      `${API_BASE_PATH}/v1/hotels/${hotelId}/pre_bookings.json`,
      params,
    );
    return {
      preBooking: result.data.preBooking,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postPreBookingPromotion = createAsyncThunk<
  {
    preBooking: PreBooking;
  },
  {
    id: string;
    hotelId: string;
    param: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postPreBookingPromotion`, async (args, { rejectWithValue }) => {
  try {
    const { id, hotelId, param } = args;
    const result = await createWrapper().post(
      `${API_BASE_PATH}/v1/hotels/${hotelId}/pre_bookings/${id}/promotions.json`,
      { pre_booking: param },
    );
    return {
      preBooking: result.data.preBooking,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postPreBookingConfirmation = createAsyncThunk<
  {
    preBooking: PreBooking;
  },
  {
    id: string;
    hotelId: string;
    param: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postPreBookingConfirmation`, async (args, { rejectWithValue }) => {
  try {
    const { id, hotelId, param } = args;
    const result = await createWrapper().post(
      `${API_BASE_PATH}/v1/hotels/${hotelId}/pre_bookings/${id}/confirmation.json`,
      { pre_booking: param },
    );
    return {
      preBooking: result.data.preBooking,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const fetchZipCodeAddress = createAsyncThunk<
  {
    address: string;
  },
  {
    id: string;
    hotelId: string;
    zipCode: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchZipCodeAddress`, async (args, { rejectWithValue }) => {
  try {
    const { id, hotelId, zipCode } = args;
    const result = await createWrapper().get(
      `${API_BASE_PATH}/v1/hotels/${hotelId}/pre_bookings/${id}/zip_codes/${zipCode}.json`,
    );
    return {
      address: result.data.address,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postPayment = createAsyncThunk<
  {
    redirectUrl: string;
  },
  { hotelId: string; param: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postBooking`, async (args, { rejectWithValue }) => {
  try {
    const { param, hotelId } = args;
    const result = await createWrapper().post(
      `${API_BASE_PATH}/v1/hotels/${hotelId}/pre_bookings/${param.pre_booking_id}/payments.json`,
      {
        booking: param,
      },
    );
    return {
      redirectUrl: result.data.redirectUrl,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

/** slice * */
export const preBookingSlice = createSlice({
  name: key,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        const preBooking = deepMerge(state, action.payload.preBooking);
        return { ...state, ...preBooking };
      })
      .addCase(fetchPreBooking.fulfilled, (state, action) => {
        state.preBooking = action.payload.preBooking;
      })
      .addCase(postPreBookingPromotion.fulfilled, (state, action) => {
        state.preBooking = action.payload.preBooking;
      })
      .addCase(postPreBookingConfirmation.fulfilled, (state, action) => {
        state.preBooking = action.payload.preBooking;
      })
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.loading = true;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action: any) => {
        state.error = action.payload?.error_messages;
        state.loading = false;
      });
  },
});

/** selector * */
const stateSelector = (state: RootState) => state[key];

export const preBookingSelector = createSelector(
  stateSelector,
  (state) => state.preBooking,
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.loading,
);

export const memberDiscountAmountSelector = createSelector(
  stateSelector,
  (state) => state.preBooking?.memberDiscountAmount,
);

export const memberDiscountAmountCentsSelector = createSelector(
  stateSelector,
  (state) => state.preBooking?.memberDiscountAmountCents,
);

export const commonScriptSelector = createSelector(
  stateSelector,
  (state) => state.preBooking?.script,
);

export const commonNoscriptSelector = createSelector(
  stateSelector,
  (state) => state.preBooking?.noscript,
);

export const preBookingScriptSelector = createSelector(
  stateSelector,
  (state) => state.preBooking?.preBookingScript,
);

export const preBookingNoscriptSelector = createSelector(
  stateSelector,
  (state) => state.preBooking?.preBookingNoscript,
);

export const preBookingTranslationTypeSelector = createSelector(
  stateSelector,
  (state) => {
    switch (state.preBooking?.hotel?.facilityType) {
      case HotelFacilityTypes.activities:
        return BookingTranslationTypes.bookingActivity;
      case HotelFacilityTypes.camping:
        return BookingTranslationTypes.bookingCamp;
      default:
        return BookingTranslationTypes.booking;
    }
  },
);

// action export
